// client/components/Dashboard/WelcomeWisdomDaily.jsx
// ============================================================================
// 🏢 Enterprise Daily Quote Widget
// Lädt Quote vom Backend, Fallback auf statische Liste wenn API nicht verfügbar
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { useTheme } from "../../context/ThemeContext.jsx";
import api from "../../constants/api.js";
import AppButton from "../ui/AppButton.jsx";

/* ───────── CONFIG ───────── */
const AUTO_HIDE_MS = 30_000; // 30s Auto-weg
const API_TIMEOUT_MS = 3000; // 3s Timeout für API

/* ───────── HELPERS ───────── */
const TODAY = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const safeLocal = {
  get(k) {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(k) : null;
    } catch {
      return null;
    }
  },
  set(k, v) {
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(k, v);
    } catch {}
  }
};
const firstNameOf = user => {
  const cand = user?.vorname || user?.name || user?.email || "Freund";
  return String(cand).trim().split(/\s+/)[0];
};
const personalize = (text, name) => {
  if (!text) return text;
  if (text.includes("{name}")) return text.replaceAll("{name}", name);
  return `${name}, ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};

/* ───────── FALLBACK QUOTES (Subset für Offline/Error) ───────── */
const FALLBACK_QUOTES = [{
  text: "Ohne Plan kein Strom – ohne Strom kein Plan.",
  author: "Altgeselle H.",
  icon: "⚡",
  category: "elektrik"
}, {
  text: "Messen statt raten – sonst wird's dunkel.",
  author: "Prüfmeisterin K.",
  icon: "🧪",
  category: "elektrik"
}, {
  text: "{name}, Schutzleiter zuerst – Probleme später.",
  author: "PE-Peter",
  icon: "🟩",
  category: "elektrik"
}, {
  text: "Strom ist ehrlich – du musst nur zuhören.",
  author: "Ohm-Opal",
  icon: "👂",
  category: "elektrik"
}, {
  text: "Schön verklemmt ist halb verkauft.",
  author: "Meister M.",
  icon: "🧰",
  category: "elektrik"
}, {
  text: "Klarheit vor Geschwindigkeit.",
  author: "Clarity C.",
  icon: "🧭",
  category: "allgemein"
}, {
  text: "Langsam ist smooth, smooth ist schnell.",
  author: "Coach K.",
  icon: "🏃",
  category: "allgemein"
}, {
  text: "Du wirst, was du wiederholst.",
  author: "Habit H.",
  icon: "🔁",
  category: "allgemein"
}, {
  text: "Perfekt ist der Feind von fertig.",
  author: "Ship-It Sam",
  icon: "🚢",
  category: "allgemein"
}, {
  text: "Qualität ist Höflichkeit am Kunden.",
  author: "Q-Klaus",
  icon: "🏆",
  category: "allgemein"
}];

/**
 * Fallback-Auswahl wenn Backend nicht erreichbar
 */
function pickFallbackQuote() {
  const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24)); // Tag seit Epoch
  const idx = day % FALLBACK_QUOTES.length;
  return FALLBACK_QUOTES[idx];
}

/**
 * Lädt Quote vom Backend
 */
async function fetchQuoteFromApi(signal) {
  try {
    const {
      data
    } = await api.get("/daily-quote/today", {
      params: {
        lang: "de"
      },
      timeout: API_TIMEOUT_MS,
      signal,
      silent: true
    });
    if (data?.ok && data?.data) {
      return {
        id: data.data.id,
        text: data.data.text,
        author: data.data.author,
        icon: data.data.icon,
        category: data.data.category,
        tone: data.data.tone,
        season: data.data.season,
        selection_reason: data.data.selection_reason,
        source: "api"
      };
    }
  } catch {
    // Silent fail, use fallback
  }
  return null;
}

/**
 * Props:
 *  - user
 *  - variant: "banner" | "inline" (default: "banner")
 */
export default function WelcomeWisdomDaily({
  user,
  variant = "banner"
}) {
    const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const firstName = useMemo(() => firstNameOf(user), [user]);

  // Quote laden (API mit Fallback)
  useEffect(() => {
    const ctrl = new AbortController();
    let mounted = true;
    async function loadQuote() {
      setLoading(true);

      // Versuche API
      const apiQuote = await fetchQuoteFromApi(ctrl.signal);
      if (!mounted) return;
      if (apiQuote) {
        setQuote(apiQuote);
      } else {
        // Fallback
        const fallback = pickFallbackQuote();
        setQuote({
          ...fallback,
          source: "fallback"
        });
      }
      setLoading(false);
    }
    loadQuote();
    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  // Öffnen nach kurzem Delay (wenn nicht bereits geschlossen)
  useEffect(() => {
    if (loading || !quote) return;
    const key = `wiretrack:welcome:CLOSED:${TODAY()}:${firstName}`;
    if (safeLocal.get(key)) return;
    const t = setTimeout(() => setOpen(true), 180);
    return () => clearTimeout(t);
  }, [loading, quote, firstName]);

  // Auto-Dismiss
  const autoHideRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    autoHideRef.current = setTimeout(() => {
      try {
        safeLocal.set(`wiretrack:welcome:CLOSED:${TODAY()}:${firstName}`, "1");
      } catch {}
      setOpen(false);
    }, AUTO_HIDE_MS);
    return () => {
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
    };
  }, [open, firstName]);

  // Nicht rendern wenn geschlossen oder noch laden
  if (!quote || !open || loading) return null;
  const text = personalize(quote.text, firstName);
  const handleClose = () => {
    try {
      safeLocal.set(`wiretrack:welcome:CLOSED:${TODAY()}:${firstName}`, "1");
    } catch {}
    setOpen(false);
  };
  const handleMouseEnter = () => {
    if (autoHideRef.current) {
      clearTimeout(autoHideRef.current);
      autoHideRef.current = null;
    }
  };
  const handleMouseLeave = () => {
    if (!autoHideRef.current) {
      autoHideRef.current = setTimeout(() => {
        handleClose();
      }, AUTO_HIDE_MS);
    }
  };
  const Content = <>
      <div className="flex items-start gap-3 min-w-0">
        <motion.div className={`rounded-xl border px-2 py-1 text-lg leading-none shrink-0 ${"border-white/10 bg-white/5"}`} aria-hidden animate={{
        rotate: [0, -6, 0, 6, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity
      }}>
          {quote.icon || "✨"}
        </motion.div>

        <div className="flex-1 min-w-0">
          <div className="mt-0.5 flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
            <p className="text-[13px] sm:text-base leading-snug">
              {text}{" "}
              <span className={"text-white/50"}>
                — {quote.author || "Anonym"}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <AppButton type="button" variant="ghost" onClick={handleClose} className={"inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs sm:text-sm hover:bg-white/10 active:scale-[0.99] transition"} title="Ausblenden" aria-label="Täglichen Spruch ausblenden">
          <X className="h-4 w-4" />
          <span className="hidden sm:inline">Ausblenden</span>
        </AppButton>
      </div>
    </>;
  const prefersReduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Inline Variant
  if (variant === "inline") {
    return <div className={`rounded-xl border backdrop-blur px-3 sm:px-4 py-2 flex items-center justify-between gap-3 w-full ${"border-white/10 bg-white/5"}`} role="status" aria-live="polite">
        {Content}
      </div>;
  }

  // Banner Variant (default)
  return <AnimatePresence>
      <motion.div initial={prefersReduced ? false : {
      y: -16,
      opacity: 0,
      scale: 0.98
    }} animate={prefersReduced ? {
      opacity: 1
    } : {
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={prefersReduced ? {
      opacity: 0
    } : {
      y: -12,
      opacity: 0,
      scale: 0.98
    }} transition={prefersReduced ? {
      duration: 0.2
    } : {
      type: "spring",
      stiffness: 260,
      damping: 22
    }} className={`relative overflow-hidden rounded-2xl border backdrop-blur ${"border-white/10 bg-gradient-to-br from-sky-600/15 via-sky-500/10 to-indigo-500/10"}`} role="status" aria-live="polite">
        {/* Shimmer Effect */}
        <motion.div aria-hidden className={`pointer-events-none absolute -top-1/2 left-0 h-[200%] w-1/2 rotate-12 blur-2xl ${"bg-white/5"}`} animate={{
        x: ["-10%", "30%", "-10%"]
      }} transition={{
        duration: 8,
        repeat: Infinity
      }} />
        
        {/* Radial Gradient */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(56,189,248,.18),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(99,102,241,.16),transparent_35%)]" />
        
        {/* Content */}
        <div className="p-3 sm:p-4 flex items-center justify-between gap-3" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onFocusCapture={handleMouseEnter} onBlurCapture={handleMouseLeave}>
          {Content}
        </div>
      </motion.div>
    </AnimatePresence>;
}